import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../api';
import type { GiftItem } from '@pickonegift/shared';
import { BackgroundType, LIMITS } from '@pickonegift/shared';
import { useSEO } from '../hooks/useSEO';

interface ItemDraft {
    title: string;
    imageUrl: string;
    productUrl: string;
    requestSize: boolean;
    requestColor: boolean;
    requestNote: boolean;
}

const emptyItem = (): ItemDraft => ({
    title: '',
    imageUrl: '',
    productUrl: '',
    requestSize: false,
    requestColor: false,
    requestNote: false,
});

export default function Create() {
    useSEO(
        'Stwórz listę prezentów — PickOneGift',
        'Zacznij tworzyć swoją wyśnioną listę prezentów i wyślij wygodny link bezpośrednio do znajomych lub rodziny.'
    );
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [creatorEmail, setCreatorEmail] = useState('');
    const [backgroundType, setBackgroundType] = useState<string>('GENERAL');
    const [b2bBranding, setB2bBranding] = useState(false);
    const [items, setItems] = useState<ItemDraft[]>([emptyItem(), emptyItem()]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetchUrlLoading, setFetchUrlLoading] = useState<number | null>(null);

    const updateItem = (index: number, updates: Partial<ItemDraft>) => {
        setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...updates } : item)));
    };

    const removeItem = (index: number) => {
        if (items.length <= LIMITS.MIN_ITEMS) return;
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const addItem = () => {
        if (items.length >= LIMITS.MAX_ITEMS) return;
        setItems((prev) => [...prev, emptyItem()]);
    };

    const fetchFromUrl = async (index: number) => {
        const url = items[index].productUrl;
        if (!url) return;
        setFetchUrlLoading(index);
        try {
            const meta = await api.parseMetadata(url);
            updateItem(index, {
                title: meta.title || items[index].title,
                imageUrl: meta.imageUrl || items[index].imageUrl,
            });
        } catch {
            // Silently fail — user can fill manually
        } finally {
            setFetchUrlLoading(null);
        }
    };

    const handleImageUpload = async (index: number, file: File) => {
        try {
            const { uploadUrl, publicUrl } = await api.presignUpload({
                filename: file.name,
                contentType: file.type,
            });
            await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            updateItem(index, { imageUrl: publicUrl });
        } catch {
            setError('Nie udało się przesłać zdjęcia');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const giftItems: GiftItem[] = items.map((item) => ({
                title: item.title,
                imageUrl: item.imageUrl,
                productUrl: item.productUrl || undefined,
                requestSize: item.requestSize,
                requestColor: item.requestColor,
                requestNote: item.requestNote,
            }));

            const result = await api.createGift({
                title,
                description: description || undefined,
                creatorEmail,
                backgroundType,
                items: giftItems,
                ...(b2bBranding && {
                    branding: {
                        logoUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop',
                        companyName: 'Acme Corp',
                        primaryColor: '#2563eb'
                    }
                })
            });

            navigate('/created', {
                state: {
                    giftId: result.giftId,
                    shareUrl: result.shareUrl,
                    deleteUrl: result.deleteUrl,
                    deleteToken: result.deleteToken,
                    title,
                },
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Coś poszło nie tak');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page">
            <Header />
            <div className="container" style={{ padding: '48px 24px' }}>
                <div className="text-center mb-lg">
                    <h1>Stwórz listę prezentów</h1>
                    <p className="text-muted mt-sm">
                        Dodaj prezenty, ustaw opcje i wygeneruj link do udostępnienia
                    </p>
                </div>

                <form className="create-form" onSubmit={handleSubmit}>
                    {/* Basic Info */}
                    <div className="form-section glass" style={{ padding: '32px' }}>
                        <h3 className="form-section-title">📋 Informacje podstawowe</h3>

                        <div className="form-group">
                            <label className="form-label">Tytuł listy *</label>
                            <input
                                className="form-input"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="np. Prezenty urodzinowe dla Ani"
                                maxLength={LIMITS.MAX_TITLE_LENGTH}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Opis (opcjonalnie)</label>
                            <textarea
                                className="form-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="np. Byłoby super, gdybyś wybrał/a coś z tej listy 😊"
                                maxLength={LIMITS.MAX_DESCRIPTION_LENGTH}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Twój email *</label>
                                <input
                                    className="form-input"
                                    type="email"
                                    value={creatorEmail}
                                    onChange={(e) => setCreatorEmail(e.target.value)}
                                    placeholder="twoj@email.com"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Motyw / Okazja</label>
                                <select
                                    className="form-select"
                                    value={backgroundType}
                                    onChange={(e) => setBackgroundType(e.target.value)}
                                >
                                    {Object.entries(BackgroundType).map(([key, val]) => (
                                        <option key={key} value={val as string}>
                                            {key === 'GENERAL' ? 'Ogólny' : key === 'BIRTHDAY' ? 'Urodziny' : 'Święta'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* B2B Demo Branding Toggle */}
                        <div className="form-group" style={{ marginTop: '8px', padding: '16px', borderRadius: '12px', background: 'rgba(124, 92, 252, 0.1)', border: '1px solid rgba(124, 92, 252, 0.2)' }}>
                            <label className="form-checkbox" style={{ fontWeight: 600 }}>
                                <input
                                    type="checkbox"
                                    checked={b2bBranding}
                                    onChange={(e) => setB2bBranding(e.target.checked)}
                                />
                                💼 Symuluj markę firmową (B2B Onboarding)
                            </label>
                            {b2bBranding && (
                                <p className="text-muted mt-sm" style={{ fontSize: '0.85rem', marginLeft: '26px' }}>
                                    Włączyłeś tryb paczki powitalnej dla pracownika. Odbiorca zobaczy interfejs z logo firmy (Acme Corp) i jej głównym kolorem (#2563eb) zamiast standardowego wyglądu PickOneGift.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Items */}
                    <div className="form-section glass" style={{ padding: '32px' }}>
                        <div className="item-builder-header">
                            <h3 className="form-section-title">🎁 Prezenty ({items.length}/{LIMITS.MAX_ITEMS})</h3>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={addItem}
                                disabled={items.length >= LIMITS.MAX_ITEMS}
                            >
                                + Dodaj prezent
                            </button>
                        </div>

                        <div className="items-list">
                            {items.map((item, index) => (
                                <div key={index} className="item-card-form fade-in">
                                    {/* Card header with number and delete */}
                                    <div className="item-card-form-header">
                                        <span className="item-card-form-number">Prezent {index + 1}</span>
                                        <button
                                            type="button"
                                            className="item-card-form-remove"
                                            onClick={() => removeItem(index)}
                                            disabled={items.length <= LIMITS.MIN_ITEMS}
                                            title="Usuń prezent"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="item-card-form-body">
                                        {/* Image preview + upload */}
                                        <div className="item-image-section">
                                            <div className="item-image-preview">
                                                {item.imageUrl ? (
                                                    <img src={item.imageUrl} alt={item.title || 'Podgląd'} />
                                                ) : (
                                                    <div className="item-image-empty">
                                                        <span>📷</span>
                                                        <span>Brak zdjęcia</span>
                                                    </div>
                                                )}
                                            </div>
                                            <label className="upload-btn">
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleImageUpload(index, file);
                                                    }}
                                                />
                                                📁 Prześlij zdjęcie
                                            </label>
                                        </div>

                                        {/* Fields */}
                                        <div className="item-fields-section">
                                            <div className="form-group">
                                                <label className="form-label">Nazwa prezentu *</label>
                                                <input
                                                    className="form-input"
                                                    type="text"
                                                    value={item.title}
                                                    onChange={(e) => updateItem(index, { title: e.target.value })}
                                                    placeholder="np. Słuchawki Sony WH-1000XM5"
                                                    maxLength={LIMITS.MAX_TITLE_LENGTH}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">URL zdjęcia *</label>
                                                <input
                                                    className="form-input"
                                                    type="url"
                                                    value={item.imageUrl}
                                                    onChange={(e) => updateItem(index, { imageUrl: e.target.value })}
                                                    placeholder="https://example.com/photo.jpg"
                                                    required
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">URL produktu (opcjonalnie)</label>
                                                <div className="input-with-action">
                                                    <input
                                                        className="form-input"
                                                        type="url"
                                                        value={item.productUrl}
                                                        onChange={(e) => updateItem(index, { productUrl: e.target.value })}
                                                        placeholder="https://sklep.pl/produkt"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary input-action-btn"
                                                        onClick={() => fetchFromUrl(index)}
                                                        disabled={!item.productUrl || fetchUrlLoading === index}
                                                    >
                                                        {fetchUrlLoading === index ? '⏳ Pobieram...' : '🔍 Pobierz dane'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Option toggles */}
                                            <div className="form-group">
                                                <label className="form-label">Zapytaj odbiorcę o:</label>
                                                <div className="toggle-pills">
                                                    <button
                                                        type="button"
                                                        className={`toggle-pill ${item.requestSize ? 'active' : ''}`}
                                                        onClick={() => updateItem(index, { requestSize: !item.requestSize })}
                                                    >
                                                        📏 Rozmiar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`toggle-pill ${item.requestColor ? 'active' : ''}`}
                                                        onClick={() => updateItem(index, { requestColor: !item.requestColor })}
                                                    >
                                                        🎨 Kolor
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`toggle-pill ${item.requestNote ? 'active' : ''}`}
                                                        onClick={() => updateItem(index, { requestNote: !item.requestNote })}
                                                    >
                                                        📝 Notatka
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <div style={{ color: 'var(--color-error)', textAlign: 'center', padding: '12px' }}>
                            ⚠️ {error}
                        </div>
                    )}

                    <div className="text-center">
                        <button
                            type="submit"
                            className="btn btn-primary btn-lg"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner" /> Tworzenie...
                                </>
                            ) : (
                                '🎁 Stwórz listę i wygeneruj link'
                            )}
                        </button>
                    </div>
                </form>
            </div>
            <Footer />
        </div>
    );
}
